import { useState } from "react";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import OrderSearchCard from "./OrderSearchCard";
import OrderCandidates from "./OrderCandidates";
import { OrderSummary, OrderTypeRow } from "./BankCaseDetails";
import { useOrderSearch } from "../../hooks/useOrderSearch";
import { useStateValue } from "../../context/StateValueContext";
import { apiErrorMessage } from "../../utils/apiError";
import { EvidenceOrderType } from "../../types/evidence";
import { BankCase, BankCaseOrderBody, OrderCandidate } from "../../types/evidenceBankCase";

export default function ChangeOrderDialog({ bankCase, onClose, onChange }: {
  bankCase: BankCase;
  onClose: () => void;
  /** Rejects with the API's reason when the change is refused. */
  onChange: (body: BankCaseOrderBody) => Promise<void>;
}) {
  const [{ miniCompanies }] = useStateValue();
  const { searching, searchError, found, findOrder } = useOrderSearch();
  const [picked, setPicked] = useState<OrderCandidate | null>(null);
  const [orderType, setOrderType] = useState<EvidenceOrderType | "">("");
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = (candidate: OrderCandidate) => {
    setPicked(candidate);
    setOrderType(candidate.orderType ?? "");
    setTried(false);
    setError(null);
  };

  const submit = async () => {
    setTried(true);
    if (!picked || !orderType) return;
    setSaving(true);
    setError(null);
    try {
      await onChange({
        order: { source: picked.source, accountId: picked.accountId, orderKey: picked.orderKey, orderName: picked.orderName },
        orderType,
      });
      onClose();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, "Failed to change the order"));
    } finally {
      setSaving(false);
    }
  };

  const companyName = miniCompanies.find((company) => company.id === bankCase.company_id)?.name ?? "";

  return (
    <Dialog
      open
      onClose={(_, reason) => { if (reason !== "backdropClick" && !saving) onClose(); }}
      maxWidth="md"
      fullWidth
      aria-labelledby="change-order-title"
    >
      <DialogTitle id="change-order-title">Change order</DialogTitle>
      <DialogContent>
        {/* Hidden rather than unmounted: Back finds the search as it was left. */}
        <Stack spacing={2} sx={{ pt: 1, display: picked ? "none" : "flex" }}>
          <OrderSearchCard searching={searching} onSearch={(query) => findOrder({ ...query, companyId: bankCase.company_id })} />
          {searchError && <Alert severity="error">{searchError}</Alert>}
          {found && <OrderCandidates result={found.result} searchedBy={found.by} onContinue={pick} />}
        </Stack>

        {picked && (
          <Stack spacing={1} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <OrderSummary candidate={picked} companyName={companyName}>
              <OrderTypeRow value={orderType} tried={tried} onChange={setOrderType} />
            </OrderSummary>
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
              The document is built again from the new order. Screenshots, the customer contact answer, product descriptions, section choices and an edited conclusion are dropped. The dispute details and the ARN stay.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {picked && <Button onClick={() => setPicked(null)} disabled={saving} sx={{ mr: "auto" }}>Back</Button>}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        {picked && (
          <Button variant="contained" onClick={() => { void submit(); }} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null}>
            Change order and generate
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
